import React, { useState, useCallback, useEffect } from 'react';
import { DatePicker, Input, Spin, Row, Col, Card, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useModulePermission } from '../../hooks/usePermission';
import LookupField from '../../components/LookupField';
import './PWM202Form.css';

import { usePWM202Mode } from './hooks/usePWM202Mode';
import { usePWM202Record } from './hooks/usePWM202Record';
import { usePWM202Navigation } from './hooks/usePWM202Navigation';
import ActionBar from './components/ActionBar';
import DetailGrid from './components/DetailGrid';
import type { FM120, FM121 } from '../../services/pwm202Api';

// ====================================================================
// Empty master template
// ====================================================================

const EMPTY_MASTER: Partial<FM120> = {
  fm120_01: '',
  fm120_11: new Date().toISOString(),
  fm120_12: '',
  fm120_13: '',
  fm120_14: '',
  fm120_15: '',
  fm120_16: 0,
  fm120_17: '',
  fm120_19: false,
  fm120_20: '',
  fm120_80: 'N',
  fm120_87: '',
  fm120_88: null,
  fm120_89: '',
  fm120_91: '',
  fm120_92: null,
  fm120_93: '',
  fm120_21: '',
  fm120_23: '',
};

// ====================================================================
// PWM202Form — 庫位移轉單
// ====================================================================

const PWM202Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwm202f1');

  const { mode, toBrowse, toAdd, toEdit, isEditing } = usePWM202Mode();
  const { record, loading, saving, load, save, remove } = usePWM202Record();
  const {
    list, hasPrev, hasNext,
    fetchList, setCurrentById,
    goFirst, goLast, goPrev, goNext,
  } = usePWM202Navigation();

  const [formData, setFormData] = useState<Partial<FM120>>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<Partial<FM121>[]>([]);
  const [selectedDetailIdx, setSelectedDetailIdx] = useState(-1);
  const [revertSnapshot, setRevertSnapshot] = useState<{ formData: Partial<FM120>; details: Partial<FM121>[] } | null>(null);

  // Sync record → form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
      setSelectedDetailIdx(-1);
    }
  }, [record]);

  // Load list on mount
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ----------------------------------------------------------------
  // Field helpers
  // ----------------------------------------------------------------

  const getField = useCallback(<K extends keyof FM120>(k: K): FM120[K] => (formData[k] ?? '') as FM120[K], [formData]);

  const setField = useCallback(<K extends keyof FM120>(k: K, v: FM120[K]) => {
    setFormData(prev => ({ ...prev, [k]: v }));
  }, []);

  // ----------------------------------------------------------------
  // Navigation
  // ----------------------------------------------------------------

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) setCurrentById(result.fm120_01);
    return result;
  }, [load, setCurrentById]);

  const handleFirst = useCallback(async () => {
    const id = goFirst();
    if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev();
    if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext();
    if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast();
    if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  // ----------------------------------------------------------------
  // CRUD
  // ----------------------------------------------------------------

  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fm120_11: new Date().toISOString() });
    setDetails([]);
    setSelectedDetailIdx(-1);
    toAdd();
  }, [toAdd]);

  const handleEdit = useCallback(() => {
    setRevertSnapshot({ formData: { ...formData }, details: details.map(d => ({ ...d })) });
    toEdit();
  }, [formData, details, toEdit]);

  const handleRevert = useCallback(() => {
    if (revertSnapshot) {
      setFormData(revertSnapshot.formData);
      setDetails(revertSnapshot.details);
    }
    setRevertSnapshot(null);
    toBrowse();
  }, [revertSnapshot, toBrowse]);

  const handleSave = useCallback(async () => {
    const isNew = mode === 'add';
    const { success, result, errors } = await save(formData, details, isNew);
    if (success && result) {
      setFormData({ ...result });
      setDetails(result.details ? result.details.map(d => ({ ...d })) : []);
      toBrowse();
      await fetchList();
      setCurrentById(result.fm120_01);
    } else if (errors && errors.length > 0) {
      const msgs = errors.map(e => e.message).join('\n');
      Modal.error({ title: '資料驗證失敗', content: <pre style={{ whiteSpace: 'pre-wrap' }}>{msgs}</pre> });
    }
  }, [mode, formData, details, save, toBrowse, fetchList, setCurrentById]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確認刪除移轉單號 ${record.fm120_01}？`,
      okType: 'danger',
      onOk: async () => {
        const ok = await remove(record.fm120_01);
        if (ok) {
          setFormData({ ...EMPTY_MASTER });
          setDetails([]);
          toBrowse();
          await fetchList();
        }
      },
    });
  }, [record, remove, toBrowse, fetchList]);

  // ----------------------------------------------------------------
  // Detail helpers
  // ----------------------------------------------------------------

  const handleAddDetail = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fm121_01: formData.fm120_01 ?? '',
        fm121_02: prev.length + 1,
        fm121_11: '',
        fm121_12: '',
        fm121_13: '',
        fm121_14: '',
        fm121_15: 0,
        fm121_16: 1,
        fm121_17: '',
        fm121_18: 0,
        fm121_181: 0,
        fm121_19: '',
        fm121_20: '',
        fm121_21: '',
        fm121_22: '',
        fm121_23: '',
        fm121_28: '',
        fm121_91: '',
        fm121_92: null,
        fm121_93: '',
        sizes: [],
      },
    ]);
  }, [formData.fm120_01]);

  const handleDeleteDetail = useCallback((idx: number) => {
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // Reindex fm121_02 (T-F05)
      return next.map((d, i) => ({ ...d, fm121_02: i + 1 }));
    });
    setSelectedDetailIdx(-1);
  }, []);

  const handleDetailChange = useCallback((idx: number, field: keyof FM121, value: unknown) => {
    setDetails(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  }, []);

  // ----------------------------------------------------------------
  // Permissions
  // ----------------------------------------------------------------

  const canCreate = permissions?.canCreate ?? false;
  const canEdit = permissions?.canEdit ?? false;
  const canDelete = permissions?.canDelete ?? false;

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  return (
    <div className="pwm202-form">
      <Spin spinning={loading}>
        <div className="pwm202-page-wrapper">
          {/* Header Info */}
          <Card size="small" className="pwm202-header-card">
            <Row gutter={[12, 8]}>
              <Col span={6}>
                <label>移轉單號</label>
                <Input
                  value={getField('fm120_01') as string}
                  disabled
                  size="small"
                  placeholder="*新增*"
                />
              </Col>
              <Col span={6}>
                <label>移轉日期 <span style={{ color: 'red' }}>*</span></label>
                <DatePicker
                  value={formData.fm120_11 ? dayjs(formData.fm120_11) : null}
                  onChange={date => setField('fm120_11', date ? date.toISOString() : null)}
                  disabled={!isEditing}
                  size="small"
                  style={{ width: '100%' }}
                  placeholder="移轉日期"
                />
              </Col>
              <Col span={6}>
                <label>入庫庫位 <span style={{ color: 'red' }}>*</span></label>
                <LookupField
                  lookupType="warehouse"
                  value={(getField('fm120_12') as string) || ''}
                  onChange={v => setField('fm120_12', v)}
                  onCodeConfirm={v => setField('fm120_12', v)}
                  disabled={!isEditing}
                  maxLength={5}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={6}>
                <label>部門代號 <span style={{ color: 'red' }}>*</span></label>
                <Input
                  value={getField('fm120_13') as string}
                  disabled
                  size="small"
                  placeholder="部門代號"
                />
              </Col>
              <Col span={6}>
                <label>出庫單號</label>
                <LookupField lookupType="warehouseIssue" value={(getField('fm120_15') as string) || ''}
                  onChange={(code) => setField('fm120_15', code)}
                  disabled={!isEditing} style={{ width: '100%' }} title="出庫單號搜尋" />
              </Col>
              <Col span={6}>
                <label>入單人員</label>
                <Input
                  value={getField('fm120_17') as string}
                  disabled
                  size="small"
                  placeholder="入單人員"
                />
              </Col>
              <Col span={6}>
                <label>調撥單號</label>
                <Input
                  value={getField('fm120_20') as string}
                  disabled
                  size="small"
                  placeholder="調撥單號"
                />
              </Col>
              <Col span={6}>
                <label>總項數</label>
                <Input
                  value={details.length}
                  disabled
                  size="small"
                />
              </Col>
              <Col span={12}>
                <label>備註</label>
                <Input
                  value={getField('fm120_23') as string}
                  onChange={e => setField('fm120_23', e.target.value)}
                  disabled={!isEditing}
                  size="small"
                  placeholder="備註"
                />
              </Col>
              <Col span={6}>
                <label>核准狀態</label>
                <Input
                  value={formData.fm120_80 === 'P' ? '已核准' : '未核准'}
                  disabled
                  size="small"
                  style={{ color: formData.fm120_80 === 'P' ? 'green' : undefined }}
                />
              </Col>
            </Row>
          </Card>

          {/* Detail Grid */}
          <Card size="small" className="pwm202-detail-card" title="移轉明細">
            <DetailGrid
              details={details}
              isEditing={isEditing}
              selectedIdx={selectedDetailIdx}
              onSelect={setSelectedDetailIdx}
              onDetailChange={handleDetailChange}
              onAdd={handleAddDetail}
              onDelete={handleDeleteDetail}
            />
          </Card>
        </div>
      </Spin>

      {/* Action Bar */}
      <ActionBar
        mode={mode}
        isEditing={isEditing}
        hasPrev={hasPrev}
        hasNext={hasNext}
        saving={saving}
        canCreate={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
        onFirst={handleFirst}
        onPrev={handlePrev}
        onNext={handleNext}
        onLast={handleLast}
        onAdd={handleAdd}
        onSave={handleSave}
        onEdit={handleEdit}
        onRevert={handleRevert}
        onDelete={handleDelete}
        onLeave={() => navigate(-1)}
      />
    </div>
  );
};

export default PWM202Form;
